import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AimieRobotAvatarIcon } from '../chat/ChatAvatarIcons';

const NOTICE_GAP = 12;
const NOTICE_WIDTH = 248;
const VIEWPORT_MARGIN = 12;

function getNoticePosition(anchor) {
  const rect = anchor.getBoundingClientRect();
  const fitsToLeft = rect.left >= NOTICE_WIDTH + NOTICE_GAP + VIEWPORT_MARGIN;

  if (fitsToLeft) {
    return {
      placement: 'left',
      top: rect.top + (rect.height / 2),
      left: rect.left - NOTICE_GAP,
    };
  }

  return {
    placement: 'above',
    top: rect.top - NOTICE_GAP,
    left: Math.min(
      Math.max(rect.left, VIEWPORT_MARGIN),
      window.innerWidth - NOTICE_WIDTH - VIEWPORT_MARGIN,
    ),
  };
}

function DownloadReadyNotice({
  anchorRef,
  t,
  theme = 'light',
  onClose,
}) {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return undefined;
    }

    const updatePosition = () => setPosition(getNoticePosition(anchor));
    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(anchor);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  if (!position) {
    return null;
  }

  return createPortal(
    <aside
      className={`download-ready-notice download-ready-notice--${position.placement} download-ready-notice--${theme === 'dark' ? 'dark' : 'light'}`}
      style={{ top: position.top, left: position.left }}
      role="status"
    >
      <button
        type="button"
        className="download-ready-notice__close"
      onClick={onClose}
      aria-label={t('report_download_notice_close_aria')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="download-ready-notice__message">
        <span className="download-ready-notice__avatar" aria-hidden="true">
          <AimieRobotAvatarIcon />
        </span>
        <p>{t('report_download_notice_message')}</p>
      </div>
      <div className="download-ready-notice__settings-hint">
        Hide or restore these: <strong>☰ → Settings → Suggestions</strong>
      </div>
    </aside>,
    document.body,
  );
}

export default DownloadReadyNotice;
